import type { PlanValues } from 'api/elplan/elplan.types'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/atoms'
import Icon from 'ui/atoms/Icon/Icon'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import DefaultTable from 'ui/components/CustomTable/CustomTable'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import Datetime from 'utils/datetime/datetime'
import { FormattedBidWithIdAndTitle } from '../../../DayAheadBidManagement/DayAheadBidManagement.helper'
import SpotBidModal from './SpotBidModal/SpotBidModal'
import styles from './SpotBidTable.module.less'
import { spotBidsToPlanValues } from './spotBidPlanValues.helper'
import { useSpotBidSave } from './useSpotBidSave'

export type SpotBidTableProps = {
  spotBids: FormattedBidWithIdAndTitle[]
  currentDate: { startTime: ISODateTime; endTime: ISODateTime }
  createBid: (bid: PlanValues[]) => Promise<unknown>
  updateBid: (bid: PlanValues[]) => Promise<unknown>
  deleteBidForDate: (date: ISODateTime) => Promise<unknown>
  retryBidList: () => void
  disabledBidButtons?: boolean
  bidListBusy?: boolean
  bidListError?: boolean
}

export default function SpotBidTable({
  spotBids,
  createBid,
  updateBid,
  deleteBidForDate,
  retryBidList,
  currentDate,
  disabledBidButtons,
  bidListBusy = false,
  bidListError = false,
}: SpotBidTableProps): ReactElement {
  const { t } = useTranslation()

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [confirmBidDeleteDialogOpen, setConfirmBidDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const saveSpotBids = useSpotBidSave({ spotBids, createBid, updateBid })

  const noRealBids = spotBids ? spotBids.length === 0 : true

  const defaultSpotBid = [
    {
      id: 0,
      fake: true,
      type: 'spotbid_without_price',
      title: t('Spot bid'),
      date: Datetime.toLocalTime(currentDate.startTime, 'onlyDate'),
      startTime: currentDate.startTime,
      endTime: currentDate.endTime,
      clear: true,
      editable: true,
    },
  ]

  async function deleteSpotBidsForDate(): Promise<void> {
    setIsDeleting(true)
    try {
      await deleteBidForDate(currentDate.startTime)
    } catch {
      return
    } finally {
      setIsDeleting(false)
    }
  }

  function handleEditClick(): void {
    if (bidListError) {
      retryBidList()
      return
    }
    setModalIsOpen(true)
  }

  const columns = [
    {
      accessor: 'color',
      header: '',
      Cell: () => {
        return <Icon color={'grey'} icon={'fa fa-circle'} />
      },
    },
    {
      accessor: 'title',
      Header: t('Spot bid'),
    },
    {
      accessor: `date`,
      Header: t(`Date`),
    },
    {
      accessor: `edit`,
      Header: t(`Edit`),
      Cell: () => (
        <Button
          link
          icon={bidListBusy || isDeleting ? 'fal fa-spinner fa-spin' : 'fal fa-pen'}
          aria-label={bidListBusy || isDeleting ? t('Loading...') : bidListError ? t('Try again') : t('Edit')}
          onClick={handleEditClick}
          disabled={disabledBidButtons || bidListBusy || isDeleting}
        >
          {''}
        </Button>
      ),
    },
    {
      accessor: `clear`,
      Header: t(`Remove`),
      Cell: () => (
        <Button
          link
          icon={'fal fa-trash'}
          aria-label={t('Remove')}
          onClick={() => setConfirmBidDeleteDialogOpen(true)}
          disabled={disabledBidButtons || bidListBusy || bidListError || isDeleting}
        >
          {''}
        </Button>
      ),
    },
  ]

  return (
    <div className={styles.ElectricityBidTable}>
      <ConfirmDialog
        open={confirmBidDeleteDialogOpen}
        textObject={{
          title: t(`Are you sure you want to remove all spotbids for {{date}}?`, {
            date: Datetime.toLocalTime(currentDate.startTime, 'onlyDate'),
          }),
          text: t(`All values for this day will be removed.`),
        }}
        onClose={() => {
          setConfirmBidDeleteDialogOpen(false)
        }}
        onConfirm={() => {
          void deleteSpotBidsForDate()
          setConfirmBidDeleteDialogOpen(false)
        }}
      />
      {disabledBidButtons && (
        <InfoBanner text={t('You cannot create or edit bids when in week view')} style={'warning'} />
      )}
      <DefaultTable columns={columns} data={defaultSpotBid ?? []} initialSortBy={[{ id: 'date', desc: false }]} />
      {modalIsOpen && (
        <SpotBidModal
          onClose={() => setModalIsOpen(false)}
          elplanExistsWarningText={t(
            'There is already an electrity plan created for this period. Your changes will not affect the current electricity plan. Would you still like to make changes?'
          )}
          title={noRealBids ? t('Create spot bid') : t('Edit spot bid')}
          planValues={spotBidsToPlanValues(spotBids, currentDate.startTime)}
          savingDisabled={bidListBusy || bidListError || isDeleting}
          onSubmit={saveSpotBids}
        />
      )}
    </div>
  )
}
